import { Box, Button, ButtonLink, Heading, Input, Stack, Text, ThemeContext } from '@vouched.id/vault';
import React, { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VouchedLogo } from '../reusable/vouched-logo';
import { set2FAData, setAuthData, setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { login } from 'store/sagas/sagas';
import { humanDate } from 'utils/ui.utils';
import { FieldState, formatInput } from 'utils/validation';
export const AdminSignin = () => {
    const { dispatch, asyncDispatch } = useGlobalState();
    const navigate = useNavigate();
    const { tokens } = useContext(ThemeContext).theme;
    const desktopLogoColor = tokens.colors.backgroundPrimary;
    const [userProfile, setUserProfile] = useState({ email: '', password: '' });
    const [isInvalid, setIsInvalid] = useState({
        email: FieldState.NOT_SET,
        password: FieldState.NOT_SET
    });
    const handleChange = useCallback((e) => {
        const { name } = e.target;
        let { value } = e.target;
        if (name === 'email') {
            const { formattedValue } = formatInput(e);
            value = formattedValue;
        }
        if (name === 'password') {
            if (value === '') {
                setIsInvalid({ [name]: FieldState.NOT_SET });
            }
            else {
                setIsInvalid({
                    [name]: FieldState.VALID
                });
            }
        }
        setUserProfile((prevState) => ({
            ...prevState,
            [name]: value
        }));
    }, []);
    const handleBlur = (e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            if (value === '') {
                setIsInvalid({ ...isInvalid, [name]: FieldState.NOT_SET });
            }
            else {
                const { isValid } = formatInput(e);
                setIsInvalid({
                    ...isInvalid,
                    [name]: isValid ? FieldState.VALID : FieldState.INVALID
                });
            }
        }
    };
    const [isLoading, setIsLoading] = useState(false);
    const submit = (event) => {
        event.preventDefault();
        if (isLoading) {
            return;
        }
        const { email } = userProfile;
        const { password } = userProfile;
        if (email && password) {
            setIsLoading(true);
            asyncDispatch(login({ email, password, recaptchaToken: 'n/a' }))
                .then((data) => {
                if ('otpToken' in data) {
                    dispatch(set2FAData(data));
                    navigate('/verify-2fa');
                }
                else if (data.user.loginAt) {
                    dispatch(setMessages({
                        severity: 'info',
                        value: `Last login at ${humanDate(data.user.loginAt)}`
                    }));
                    dispatch(setAuthData(data));
                    navigate('/account/jobs');
                }
            })
                .catch((error) => {
                setIsLoading(false);
                setIsInvalid({
                    email: FieldState.INVALID,
                    password: FieldState.INVALID
                });
                console.error('Signin Error', error);
            });
        }
    };
    // eventually update vault theme
    const midPurple = '#453FC1';
    const brightGreen = '#9FEA99';
    return (React.createElement(Box, { display: ['block', 'flex'], height: "100vh", padding: ['spacing.large', 'spacing.none'], style: { boxSizing: 'border-box', backgroundColor: midPurple }, tag: "main" },
        React.createElement(Box, { alignItems: "center", backgroundColor: "colors.backgroundPrimary", display: "flex", flex: "1", flexDirection: "column", justifyContent: "center", paddingX: "spacing.large", paddingY: "spacing.xlarge", position: "relative", style: {
                borderRadius: ['spacing.default', '0'],
                boxSizing: 'border-box',
                top: '50%',
                transform: 'translateY(-50%)'
            } },
            React.createElement(Box, { display: ['block', 'none'], marginBottom: "spacing.large", textAlign: "center" },
                React.createElement(VouchedLogo, { fill: midPurple, maxWidth: "none", width: "100%" }),
                React.createElement(Box, { marginBottom: "spacing.xlarge", marginTop: "spacing.default" },
                    React.createElement(Text, { style: {
                            color: 'colors.textMuted',
                            fontWeight: '600',
                            fontSize: ['text.size.default', null, 'text.size.large']
                        }, tag: "span" }, "It's Who You Know")),
                React.createElement(Heading, { style: {
                        fontWeight: 'bold'
                    }, tag: "h1", variation: "h3" }, "Sign In")),
            React.createElement(Box, { display: ['none', 'block'], marginBottom: "spacing.xlarge2" },
                React.createElement(Heading, { style: { fontWeight: 'bold' }, tag: "h1", variation: "h3" }, "Sign In to Your Account")),
            React.createElement(Box, { maxWidth: "52rem", noValidate: true, onSubmit: submit, tag: "form", width: "100%" },
                React.createElement(Stack, { spacing: "spacing.large" },
                    React.createElement(Box, null,
                        React.createElement(Input, { autoComplete: "email", inputMode: "email", isInvalid: isInvalid['email'] === FieldState.INVALID, label: "Email", name: "email", onBlur: handleBlur, onChange: handleChange, validationMessage: "Invalid email address or password." })),
                    React.createElement(Box, null,
                        React.createElement(Input, { inputMode: "text", isInvalid: isInvalid['password'] === FieldState.INVALID, label: "Password", name: "password", onChange: handleChange, type: "password" })),
                    React.createElement(Box, { marginTop: "spacing.small", paddingRight: "spacing.none", style: {
                            a: { height: 'initial' }
                        }, textAlign: "right" },
                        React.createElement(ButtonLink, { "data-cy": "forgot-password", href: "/forgot-password", variation: "text" }, "Forgot your Password?")),
                    React.createElement(Box, { alignItems: "center", display: "flex", justifyContent: "center", marginTop: "spacing.default", paddingTop: "spacing.default", width: "100%" },
                        React.createElement(Button, { "data-cy": "submit", style: { backgroundColor: midPurple }, title: isLoading ? 'loading' : '' }, "Sign In"))))),
        React.createElement(Box, { alignItems: "center", display: ['none', 'flex'], flex: "1", flexDirection: "column", justifyContent: "center", paddingX: "spacing.default" },
            React.createElement(VouchedLogo, { fill: desktopLogoColor, maxWidth: "50rem", width: "80%" }),
            React.createElement(Box, { marginTop: "spacing.large" },
                React.createElement(Text, { style: {
                        color: brightGreen,
                        fontWeight: '600',
                        fontSize: ['text.size.default', null, 'text.size.large']
                    }, tag: "span" }, "It's Who You Know")))));
};
